import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Tooltip from '@radix-ui/react-tooltip';
import {
  LogOut,
  MessageSquare,
  Moon,
  Settings as SettingsIcon,
  Tags,
  User as UserIcon,
} from 'lucide-react';
import type * as React from 'react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AvatarComponent from '../../../components/UI/Avatar/Avatar.component';
import { apiRoutes } from '../../../constants/api-routes.constants';
import { AuthContext } from '../../../context/AuthContext';
import { getInitials } from '../../../helpers/getInitials';
import {
  setTheme as applyThemePreference,
  type Theme,
} from '../../../helpers/theme';
import { useLogoutFlow } from '../../../hooks/useLogoutFlow';
import type { UserState } from '../../../interfaces/user.interface';
import type { UserAvatarState } from '../../../interfaces/userPreferences.interface';
import { getAvatarUrl } from '../../../services/getPresignedUrlREST';
import { userAvatarSlice } from '../../../store/userAvatar.store';
import './avatar-menu.scss';

interface AvatarMenuProps {
  firstName: string;
  lastName: string;
}

const AvatarMenu = (props: AvatarMenuProps): React.ReactElement => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // TRIL-141: identity-check guard reads userAvatarKey at commit time
  // (after the await) so a stale promise can't win the last write even if
  // it resolves between the Redux state change (logout/relogin) and React
  // running passive cleanup. The store is the source of truth at commit
  // time; signal.aborted / refs are not enough.
  const store = useStore() as {
    getState: () => { userAvatarStore: { userAvatarKey: string | null } };
  };
  const {
    userAvatarUrl,
    userAvatarKey,
    reloadAvatar,
    loadingAvatar,
    newAvatar,
    newAvatarUrl,
  } = useSelector((state: UserAvatarState) => state.userAvatarStore);
  const { user } = useSelector((state: UserState) => state.userStore);
  const { user: authUser } = useContext(AuthContext);
  // TRIL-147: fall back to AuthContext username (synchronously available
  // from JWT decode at AuthProvider mount, sourced from localStorage
  // `userData` when token+persisted ids match) when Redux user.username is
  // empty. This keeps the trigger functional during the hydration gap and
  // during retry windows where Redux stays empty longer than expected.
  const effectiveUsername = user.username || authUser?.username || '';
  const userInitials = getInitials(props);
  const { SET_USER_AVATAR_URL } = userAvatarSlice.actions;
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [theUserAvatarKey, setTheUserAvatarKey] = useState<string | null>(null);

  useEffect(() => {
    if (newAvatarUrl && userAvatarKey) {
      setAvatarUrl(newAvatarUrl);
      setTheUserAvatarKey(userAvatarKey);
    } else {
      setAvatarUrl(userAvatarUrl);
      setTheUserAvatarKey(userAvatarKey);
    }
  }, [newAvatarUrl, userAvatarUrl, userAvatarKey]);

  const handleAvatarError = useCallback(async () => {
    if (!theUserAvatarKey) return;
    const capturedKey = theUserAvatarKey;
    // TRIL-306: prefer the hydrated server URL (userPreferences.avatarUrl) over
    // the client getAvatarUrl presign endpoint — unprovisioned in some envs
    // (VITE_API_GET_USER_AVATAR unset → ERR_NAME_NOT_RESOLVED) — unless it's the
    // URL that just failed. The presign stays a last-resort fallback.
    const serverUrl = user.userPreferences?.avatarUrl;
    if (serverUrl && serverUrl !== avatarUrl) {
      setAvatarUrl(serverUrl);
      dispatch(SET_USER_AVATAR_URL(serverUrl));
      return;
    }
    try {
      const freshUrl = await getAvatarUrl(capturedKey);
      // Identity check at commit time: the key may have changed (logout
      // reset the slice, or a new user logged in) while the presign was in
      // flight. Comparing against the live store catches the race window
      // before React runs the next render/effect cleanup.
      if (store.getState().userAvatarStore.userAvatarKey !== capturedKey) {
        return;
      }
      setAvatarUrl(freshUrl);
      dispatch(SET_USER_AVATAR_URL(freshUrl));
    } catch {
      // leave avatar as null — initials will show
    }
  }, [
    theUserAvatarKey,
    avatarUrl,
    user.userPreferences?.avatarUrl,
    dispatch,
    SET_USER_AVATAR_URL,
    store,
  ]);

  const handleViewProfile = useCallback(() => {
    if (!effectiveUsername) return;
    navigate(`${apiRoutes.USER}/${effectiveUsername}/profile`);
  }, [navigate, effectiveUsername]);

  const handleSettings = useCallback(() => {
    if (!effectiveUsername) return;
    navigate(`${apiRoutes.USER}/${effectiveUsername}${apiRoutes.SETTINGS}`);
  }, [navigate, effectiveUsername]);

  // TRIL-322: entry point to edit interest categories after onboarding.
  const handleEditInterests = useCallback(() => {
    if (!effectiveUsername) return;
    navigate(`${apiRoutes.USER}/${effectiveUsername}${apiRoutes.INTERESTS}`);
  }, [navigate, effectiveUsername]);

  // TRIL-394: entry point to direct messages.
  const handleMessages = useCallback(() => {
    if (!effectiveUsername) return;
    navigate(`${apiRoutes.USER}/${effectiveUsername}/direct-messages`);
  }, [navigate, effectiveUsername]);

  const handleToggleTheme = useCallback(() => {
    const current =
      (localStorage.getItem('trilirivos-theme') as Theme | null) ?? 'system';
    const isDark =
      current === 'dark' ||
      (current === 'system' &&
        typeof window !== 'undefined' &&
        window.matchMedia('(prefers-color-scheme: dark)').matches);
    applyThemePreference(isDark ? 'light' : 'dark');
  }, []);

  // TRIL-146: useLogoutFlow is the canonical logout trigger shared between
  // the AvatarMenu Sign-out item and the NavigationRail Logout button.
  // Replaces the asChild+div+<Logout/> nesting pattern that had keyboard/
  // Radix interaction risk (memory IDs 14089/14121/14145).
  const triggerLogout = useLogoutFlow();

  // TRIL-146: Tooltip.Provider is component-local. Production renders
  // AvatarMenu as a sibling of NavigationRail inside GlobalHeader, so the
  // rail's internal Tooltip.Provider does NOT reach AvatarMenu. Without
  // this local Provider, Radix throws at mount-time → crashes the signed-
  // in header. (Found in PR #106 review.) Nested Tooltip.Providers are
  // safe — the innermost wins for delayDuration, no perf cost.
  return (
    <DropdownMenu.Root>
      <Tooltip.Provider delayDuration={150}>
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                aria-label={
                  effectiveUsername
                    ? `Account menu — ${effectiveUsername}`
                    : 'Account menu'
                }
                className={`avatar-menu__trigger User__Avatar${!avatarUrl ? '__Initials' : ''}`}
                disabled={!effectiveUsername}
              >
                {loadingAvatar ? (
                  userInitials
                ) : (
                  <AvatarComponent
                    username={user.username}
                    firstName={props.firstName}
                    lastName={props.lastName}
                    newAvatar={newAvatar as string}
                    reloadAvatar={reloadAvatar}
                    userAvatarKey={theUserAvatarKey as string}
                    userAvatarUrl={avatarUrl as string}
                    onImageError={handleAvatarError}
                  />
                )}
              </button>
            </DropdownMenu.Trigger>
          </Tooltip.Trigger>
          {/* TRIL-146 I2: username surfaces as a sighted-hover tooltip. The
              canonical a11y signal is `alt={username}` on the Avatar img
              element (handled by AvatarComponent) — the tooltip is a
              sighted-user convenience that supplements, not replaces, the
              alt-text. */}
          {effectiveUsername && (
            <Tooltip.Portal>
              <Tooltip.Content
                side="bottom"
                sideOffset={6}
                className="avatar-menu__username-tooltip"
              >
                {effectiveUsername}
              </Tooltip.Content>
            </Tooltip.Portal>
          )}
        </Tooltip.Root>
      </Tooltip.Provider>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="avatar-menu__content"
          align="end"
          sideOffset={6}
        >
          <DropdownMenu.Item
            className="avatar-menu__item"
            onSelect={handleViewProfile}
          >
            <UserIcon size={14} />
            <span>View profile</span>
          </DropdownMenu.Item>
          <DropdownMenu.Item
            className="avatar-menu__item"
            onSelect={handleSettings}
          >
            <SettingsIcon size={14} />
            <span>Settings</span>
          </DropdownMenu.Item>
          <DropdownMenu.Item
            className="avatar-menu__item"
            onSelect={handleEditInterests}
          >
            <Tags size={14} />
            <span>Edit interests</span>
          </DropdownMenu.Item>
          <DropdownMenu.Item
            className="avatar-menu__item"
            onSelect={handleMessages}
          >
            <MessageSquare size={14} />
            <span>Messages</span>
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="avatar-menu__separator" />
          <DropdownMenu.Item
            className="avatar-menu__item"
            onSelect={handleToggleTheme}
          >
            <Moon size={14} />
            <span>Theme</span>
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="avatar-menu__separator" />
          {/* TRIL-146 I1/I4: Sign out is a normal DropdownMenu.Item with an
              onSelect that fires the canonical logout flow. Replaces the
              earlier asChild+div+<Logout/> wrapper that had a Radix focus
              + keyboard interaction risk. Spacing now matches the other
              items because the layout is identical. */}
          <DropdownMenu.Item
            className="avatar-menu__item avatar-menu__item--danger"
            onSelect={() => void triggerLogout()}
          >
            <LogOut size={14} />
            <span>Sign out</span>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default AvatarMenu;
